// src/context/AuthContext.js
// UPDATED: 
// - 3-day trial duration
// - Better hasUsedTrial detection
// - Added weekly offering support for RevenueCat
// - 🔥 FIXED: Premium access retained until premiumUntil date (even after cancellation)
// - 🔥 ADDED: subscriptionCancelled flag and premiumEndsAt for UI

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, onSnapshot, setDoc, updateDoc } from 'firebase/firestore'; 
import { Capacitor } from '@capacitor/core'; 
import { Purchases } from '@revenuecat/purchases-capacitor'; 

// 🔐 REVENUECAT CONFIG
const REVENUECAT_API_KEY = "appl_hSKbqCBlSgaNGjXoYyIqGPOommJ"; 
const ENTITLEMENT_ID = "Aipredict Premium";

// 🔥 TRIAL CONFIG
export const TRIAL_DURATION_DAYS = 3;

const AuthContext = createContext(null);

// 🔥 SINGLETON: Track if RevenueCat is already configured
let revenueCatConfigured = false;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [profileLoading, setProfileLoading] = useState(true);

    // ⚡ RevenueCat Status (non-blocking)
    const [isRevenueCatPremium, setIsRevenueCatPremium] = useState(false);
    const [revenueCatChecked, setRevenueCatChecked] = useState(false);
    const [offerings, setOfferings] = useState(null);
    
    // Refs to prevent stale closures
    const userRef = useRef(null);
    const profileRef = useRef(null);
    
    useEffect(() => { userRef.current = user; }, [user]);
    useEffect(() => { profileRef.current = profile; }, [profile]);

    // Helper: Check premium from customerInfo
    const checkPremiumFromCustomerInfo = useCallback((customerInfo) => {
        if (!customerInfo?.entitlements?.active) return false;
        
        const hasEntitlement = !!customerInfo.entitlements.active[ENTITLEMENT_ID];
        const hasAnyPremium = Object.keys(customerInfo.entitlements.active).some(
            key => key.toLowerCase().includes('premium')
        );
        const hasActiveSub = customerInfo.activeSubscriptions?.some(
            sub => sub.includes("com.aipredict.app")
        );

        return hasEntitlement || hasAnyPremium || hasActiveSub;
    }, []);

    // 🔥 SYNC TO FIREBASE (fire-and-forget, non-blocking)
    const syncPremiumToFirebase = useCallback(async (uid, isPremiumStatus, customerInfo) => {
        if (!uid) return;
        
        try {
            const userDocRef = doc(db, 'users', uid);
            
            if (isPremiumStatus) {
                let expirationDate = null;
                const entitlement = customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
                if (entitlement?.expirationDate) {
                    expirationDate = new Date(entitlement.expirationDate);
                }
                
                updateDoc(userDocRef, {
                    isPremium: true,
                    plan: 'premium',
                    premiumSource: 'apple',
                    premiumUntil: expirationDate,
                    lastRevenueCatSync: new Date(),
                    trialActive: false,
                }).catch(() => {
                    setDoc(userDocRef, {
                        isPremium: true,
                        plan: 'premium',
                        premiumSource: 'apple',
                        premiumUntil: expirationDate,
                        lastRevenueCatSync: new Date(),
                        trialActive: false,
                    }, { merge: true }).catch(console.error);
                });
            }
        } catch (e) {
            console.error("Firebase sync error:", e);
        }
    }, []);

    // 🔥 CHECK REVENUECAT STATUS
    const checkRevenueCatStatus = useCallback(async (uid = null) => {
        if (Capacitor.getPlatform() !== 'ios') {
            setRevenueCatChecked(true);
            return false;
        }

        try {
            const { customerInfo } = await Purchases.getCustomerInfo();
            const isPremium = checkPremiumFromCustomerInfo(customerInfo);

            setIsRevenueCatPremium(isPremium);
            setRevenueCatChecked(true);

            if (isPremium && (uid || userRef.current?.uid)) {
                syncPremiumToFirebase(uid || userRef.current.uid, true, customerInfo);
            }

            return isPremium;
        } catch (e) {
            console.error("RevenueCat Check Failed:", e);
            setRevenueCatChecked(true);
            return false;
        }
    }, [checkPremiumFromCustomerInfo, syncPremiumToFirebase]);

    // 1. INITIALIZE REVENUECAT (Once only, non-blocking)
    useEffect(() => {
        if (Capacitor.getPlatform() !== 'ios') {
            setRevenueCatChecked(true);
            return;
        }

        let listenerHandle = null;
        let mounted = true;

        const initRC = async () => {
            try {
                if (!revenueCatConfigured) {
                    await Purchases.configure({ apiKey: REVENUECAT_API_KEY });
                    revenueCatConfigured = true;
                    console.log("⚡ RevenueCat configured (singleton)");
                }

                const offeringsData = await Purchases.getOfferings();
                if (mounted && offeringsData.current) {
                    // 🔥 UPDATED: Extract all packages including weekly
                    const currentOffering = offeringsData.current;
                    
                    // Find packages by identifier
                    const weeklyPkg = currentOffering.availablePackages?.find(
                        pkg => pkg.packageType === 'WEEKLY' || 
                               pkg.identifier?.toLowerCase().includes('weekly') ||
                               pkg.identifier === '$rc_weekly'
                    );
                    const monthlyPkg = currentOffering.availablePackages?.find(
                        pkg => pkg.packageType === 'MONTHLY' || 
                               pkg.identifier?.toLowerCase().includes('monthly') ||
                               pkg.identifier === '$rc_monthly'
                    );
                    const annualPkg = currentOffering.availablePackages?.find(
                        pkg => pkg.packageType === 'ANNUAL' || 
                               pkg.identifier?.toLowerCase().includes('annual') ||
                               pkg.identifier?.toLowerCase().includes('yearly') ||
                               pkg.identifier === '$rc_annual'
                    );
                    
                    setOfferings({
                        weekly: weeklyPkg || null,      // 🔥 NEW
                        monthly: monthlyPkg || currentOffering.monthly || null,
                        annual: annualPkg || currentOffering.annual || null,
                        all: currentOffering.availablePackages || [],
                        raw: currentOffering
                    });
                    
                    console.log("📦 RevenueCat Offerings loaded:", {
                        weekly: weeklyPkg?.identifier || 'not found',
                        monthly: monthlyPkg?.identifier || currentOffering.monthly?.identifier || 'not found',
                        annual: annualPkg?.identifier || currentOffering.annual?.identifier || 'not found',
                        allPackages: currentOffering.availablePackages?.map(p => p.identifier)
                    });
                }

                const { customerInfo } = await Purchases.getCustomerInfo();
                if (mounted) {
                    const isPremium = checkPremiumFromCustomerInfo(customerInfo);
                    setIsRevenueCatPremium(isPremium);
                    setRevenueCatChecked(true);
                }

                listenerHandle = await Purchases.addCustomerInfoUpdateListener((info) => {
                    if (!mounted) return;
                    const isPremium = checkPremiumFromCustomerInfo(info);
                    setIsRevenueCatPremium(isPremium);
                    
                    if (isPremium && userRef.current?.uid) {
                        syncPremiumToFirebase(userRef.current.uid, true, info);
                    }
                });

            } catch (e) {
                console.error("RevenueCat Init Error:", e);
                if (mounted) setRevenueCatChecked(true);
            }
        };

        initRC();

        return () => {
            mounted = false;
            if (listenerHandle?.remove) listenerHandle.remove();
        };
    }, [checkPremiumFromCustomerInfo, syncPremiumToFirebase]);

    // 2. FIREBASE AUTH LISTENER
    useEffect(() => {
        let unsubProfile = null;

        const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
            if (unsubProfile) unsubProfile();

            if (!firebaseUser) {
                setUser(null);
                setProfile(null);
                setLoading(false);
                setProfileLoading(false);
                return;
            }

            setUser(firebaseUser);
            setLoading(false);
            setProfileLoading(true);

            if (Capacitor.getPlatform() === 'ios' && revenueCatConfigured) {
                Purchases.logIn({ appUserID: firebaseUser.uid })
                    .then(() => checkRevenueCatStatus(firebaseUser.uid))
                    .catch(console.error);
            }

            const userDocRef = doc(db, 'users', firebaseUser.uid);
            
            setDoc(userDocRef, { 
                lastSeenAt: new Date(), 
                email: firebaseUser.email, 
                uid: firebaseUser.uid 
            }, { merge: true }).catch(() => {});

            unsubProfile = onSnapshot(userDocRef, (snap) => {
                setProfile(snap.exists() ? snap.data() : null);
                setProfileLoading(false);
            });
        });

        return () => {
            if (unsubProfile) unsubProfile();
            unsubAuth();
        };
    }, [checkRevenueCatStatus]);

    // =========================================================
    // 3. COMPUTED VALUES
    // =========================================================

    // 🔥 Check if trial is active
    const isTrialActive = useMemo(() => {
        if (!profile?.trialEndsAt) return false;
        const endMs = profile.trialEndsAt?.toMillis?.() || new Date(profile.trialEndsAt).getTime();
        return Date.now() < endMs;
    }, [profile?.trialEndsAt]);

    // 🔥 Calculate trial days left
    const trialDaysLeft = useMemo(() => {
        if (!profile?.trialEndsAt) return 0;
        const endMs = profile.trialEndsAt?.toMillis?.() || new Date(profile.trialEndsAt).getTime();
        const daysLeft = Math.ceil((endMs - Date.now()) / (1000 * 60 * 60 * 24));
        return Math.max(0, daysLeft);
    }, [profile?.trialEndsAt]);

    // 🔥 FIXED: Check if premiumUntil date is still valid
    const isPremiumUntilValid = useMemo(() => {
        if (!profile?.premiumUntil) return false;
        const expiryDate = profile.premiumUntil?.toDate?.() || new Date(profile.premiumUntil);
        return expiryDate > new Date();
    }, [profile?.premiumUntil]);

    // 🔥 FIXED: Get premium end date (for UI display)
    const premiumEndsAt = useMemo(() => {
        if (profile?.premiumUntil) {
            return profile.premiumUntil?.toDate?.() || new Date(profile.premiumUntil);
        }
        if (profile?.subscriptionEnd) {
            return profile.subscriptionEnd?.toDate?.() || new Date(profile.subscriptionEnd);
        }
        if (isTrialActive && profile?.trialEndsAt) {
            return profile.trialEndsAt?.toDate?.() || new Date(profile.trialEndsAt);
        }
        return null;
    }, [profile?.premiumUntil, profile?.subscriptionEnd, profile?.trialEndsAt, isTrialActive]);

    // 🔥 NEW: Check if subscription was cancelled (for UI messaging)
    const isSubscriptionCancelled = useMemo(() => {
        return profile?.subscriptionCancelled === true;
    }, [profile?.subscriptionCancelled]);

    // 🔥 FIXED: isPremium - Check premiumUntil FIRST (highest priority)
    const isPremium = useMemo(() => {
        // 1. RevenueCat takes highest priority (iOS)
        if (isRevenueCatPremium) return true;
        
        // 2. 🔥 FIXED: Check premiumUntil date FIRST (even if subscription is cancelled)
        //    This ensures users keep access until their paid period ends
        if (isPremiumUntilValid) return true;
        
        // 3. Check active trial
        if (isTrialActive) return true;
        
        // 4. Check explicit premium flags (only if premiumUntil not set)
        if (profile?.isPremium === true && profile?.plan === 'premium') return true;
        if (profile?.plan === 'premium' && !profile?.subscriptionCancelled) return true;
        
        return false;
    }, [isRevenueCatPremium, isPremiumUntilValid, isTrialActive, profile?.isPremium, profile?.plan, profile?.subscriptionCancelled]);

    // 🔥 FIXED: Check if user is PAID premium (not trial)
    const isPaidPremium = useMemo(() => {
        // 1. RevenueCat (iOS)
        if (isRevenueCatPremium) return true;
        
        // 2. 🔥 FIXED: Check premiumUntil date (even if cancelled, they paid for this period)
        if (isPremiumUntilValid && !isTrialActive) return true;
        
        // 3. Explicit premium plan
        if (profile?.plan === 'premium') return true;
        if (profile?.isPremium === true && profile?.plan !== 'trial' && !profile?.trialEndsAt) return true;
        
        return false;
    }, [isRevenueCatPremium, isPremiumUntilValid, isTrialActive, profile?.isPremium, profile?.plan, profile?.trialEndsAt]);

    // 🔥 Calculate premium days left (for cancelled subscriptions UI)
    const premiumDaysLeft = useMemo(() => {
        if (!premiumEndsAt) return 0;
        const diffTime = premiumEndsAt - new Date();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return Math.max(0, diffDays);
    }, [premiumEndsAt]);

    const isAdmin = profile?.isAdmin === true;

    // 🔥 Better hasUsedTrial detection
    const hasUsedTrial = useMemo(() => {
        if (profile?.trialUsed === true) return true;
        if (profile?.isTrialUsed === true) return true;
        
        if (profile?.trialEndsAt) {
            const endMs = profile.trialEndsAt?.toMillis?.() || new Date(profile.trialEndsAt).getTime();
            if (Date.now() >= endMs) return true;
            return true;
        }
        
        if (profile?.trialActive === false && profile?.plan === 'trial') return true;
        if (profile?.phoneVerified === true && profile?.trialStartedAt) return true;
        
        return false;
    }, [profile?.trialUsed, profile?.isTrialUsed, profile?.trialEndsAt, profile?.trialActive, profile?.plan, profile?.phoneVerified, profile?.trialStartedAt]);

    // 🔥 Can user start a NEW trial?
    const canStartTrial = useMemo(() => {
        if (isPaidPremium) return false;
        if (isTrialActive) return false;
        if (hasUsedTrial) return false;
        return true;
    }, [isPaidPremium, isTrialActive, hasUsedTrial]);

    // 4. PURCHASE FUNCTION
    const purchasePackage = useCallback(async (packageToBuy) => {
        try {
            const { customerInfo } = await Purchases.purchasePackage({ aPackage: packageToBuy });
            const isPremium = checkPremiumFromCustomerInfo(customerInfo);
            setIsRevenueCatPremium(isPremium);
            
            if (isPremium && userRef.current?.uid) {
                await syncPremiumToFirebase(userRef.current.uid, true, customerInfo);
            }
            
            return true;
        } catch (e) {
            if (!e.userCancelled) console.error("Purchase Error:", e);
            return false;
        }
    }, [checkPremiumFromCustomerInfo, syncPremiumToFirebase]);

    // 5. RESTORE PURCHASES
    const restorePurchases = useCallback(async () => {
        try {
            const { customerInfo } = await Purchases.restorePurchases();
            const isPremium = checkPremiumFromCustomerInfo(customerInfo);
            setIsRevenueCatPremium(isPremium);
            
            if (isPremium && userRef.current?.uid) {
                await syncPremiumToFirebase(userRef.current.uid, true, customerInfo);
            }
            
            return isPremium;
        } catch (e) {
            console.error("Restore Error:", e);
            return false;
        }
    }, [checkPremiumFromCustomerInfo, syncPremiumToFirebase]);

    // 6. CONTEXT VALUE
    const value = useMemo(() => ({
        user,
        profile,
        isPremium,
        isPaidPremium,
        isTrialActive,
        trialDaysLeft,
        hasUsedTrial,
        canStartTrial,
        isAdmin,
        loading,
        profileLoading,
        isRevenueCatPremium,
        revenueCatChecked,
        offerings,
        // 🔥 NEW: Cancellation & expiry info for UI
        isSubscriptionCancelled,
        premiumEndsAt,
        premiumDaysLeft,
        signOutUser: () => signOut(auth),
        refreshPremiumStatus: () => checkRevenueCatStatus(userRef.current?.uid),
        purchasePackage,
        restorePurchases,
        TRIAL_DURATION_DAYS,
    }), [
        user, profile, isPremium, isPaidPremium, isTrialActive, trialDaysLeft, hasUsedTrial, canStartTrial, isAdmin, 
        loading, profileLoading, isRevenueCatPremium, revenueCatChecked,
        offerings, isSubscriptionCancelled, premiumEndsAt, premiumDaysLeft,
        checkRevenueCatStatus, purchasePackage, restorePurchases
    ]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);